import React, { useCallback, useEffect, useState } from 'react';

import { Control, useController } from 'react-hook-form';
import { theme } from '~/theme';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Autocomplete as MuiAutocomplete,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { getDynamicPaymentScheduleTemplates } from '~/services/PaymentsService';

import { ACCORDION_SECTION_IDS, useAccordionExpanded } from './AccordionExpandedContext';

interface PaymentScheduleTemplate {
  id: string;
  name: string;
  description?: string;
  payment_schedules?: Array<{
    payment_type: string;
    due_date_type: string;
    due_date_value: string;
    amount_type: string;
    amount_value: string;
    fee?: {
      value_currencies?: Array<{
        currency: string;
        amount: number;
      }>;
    };
  }>;
}

interface Props {
  control: Control<any>;
}

const SECTION_ID = ACCORDION_SECTION_IDS.DYNAMIC_PAYMENT_SCHEDULES;

function DynamicPaymentSchedulesAccordion({ control }: Props) {
  const { isSectionExpanded, toggleSection } = useAccordionExpanded();
  const [templates, setTemplates] = useState<Array<PaymentScheduleTemplate>>([]);
  const [loading, setLoading] = useState(false);

  const handleAccordionChange = useCallback(() => {
    toggleSection(SECTION_ID);
  }, [toggleSection]);

  const {
    field: { value, onChange },
  } = useController({
    control,
    name: 'dynamic_payment_schedule_template_id',
    defaultValue: [],
  });

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const response = await getDynamicPaymentScheduleTemplates();
        const schedules = response.result?.schedules || [];
        setTemplates(schedules);
      } catch (error) {
        console.error('Failed to fetch dynamic payment schedule templates:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  // Get selected templates based on form value (array of IDs)
  const selectedTemplates = templates.filter((template) => Array.isArray(value) && value.includes(template.id));

  const renderTemplateDetails = (template: PaymentScheduleTemplate) => (
    <Card key={template.id} variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Dynamic Payment Schedule Template
            </Typography>
            <Chip label={template.name} color="primary" sx={{ mt: 1 }} />
          </Box>
          {/* First table: ID, Name, Description */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{template.id}</TableCell>
                  <TableCell>{template.name}</TableCell>
                  <TableCell>{template.description || 'No description available'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          {/* Second table: Payment Schedules */}
          {template.payment_schedules && template.payment_schedules.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Payment Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Due Date Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Due Days</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Amount Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Amount Value</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Value Currencies</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {template.payment_schedules.map((schedule, index) => (
                    <TableRow key={index}>
                      <TableCell>{schedule.payment_type}</TableCell>
                      <TableCell>{schedule.due_date_type}</TableCell>
                      <TableCell>{schedule.due_date_value}</TableCell>
                      <TableCell>{schedule.amount_type}</TableCell>
                      <TableCell>{schedule.amount_value}</TableCell>
                      <TableCell>
                        {schedule.fee?.value_currencies ? (
                          <Box>
                            {schedule.fee.value_currencies.map((currency, idx) => (
                              <Typography key={idx} variant="body2">
                                {currency.currency}: {currency.amount}
                              </Typography>
                            ))}
                          </Box>
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Accordion expanded={isSectionExpanded(SECTION_ID)} onChange={handleAccordionChange} disableGutters>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        id="dynamic-payment-schedules"
        sx={{
          backgroundColor: theme.palette.grey[200],
        }}
      >
        <Typography variant="h5">Dynamic Payment Schedules</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={3}>
          <Box sx={{ position: 'relative' }}>
            <MuiAutocomplete
              options={templates.map((template) => ({ value: template.id, label: template.name }))}
              getOptionLabel={(option) => option.label}
              value={
                Array.isArray(value) && value.length > 0
                  ? templates
                      .filter((template) => value.includes(template.id))
                      .map((template) => ({ value: template.id, label: template.name }))[0] || null
                  : null
              }
              onChange={(_, selectedOption) => {
                const selectedIds = selectedOption ? [selectedOption.value] : [];
                onChange(selectedIds);
              }}
              loading={loading}
              renderInput={(params) => (
                <TextField {...params} label="Dynamic Payment Schedule Template" variant="outlined" />
              )}
            />
            {loading && (
              <CircularProgress
                size={20}
                sx={{
                  position: 'absolute',
                  right: 40,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              />
            )}
          </Box>

          {selectedTemplates.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Selected Template Details
              </Typography>
              {selectedTemplates.map(renderTemplateDetails)}
            </Box>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default DynamicPaymentSchedulesAccordion;
